import React, { useState, useMemo } from "react";
import axios from "axios";
import { Upload, Check } from "lucide-react";
import AnnouncementBar from "../components/AnnouncementBar";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { countries } from "../data/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const waitlistImage =
  "https://images.unsplash.com/flagged/photo-1578317767641-c2a23b16b814?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHx3ZWRkaW5nJTIwZHJlc3MlMjBzaWxob3VldHRlfGVufDB8fHx8MTc4ODE1MjgwMHww&ixlib=rb-4.1.0&q=85";

const occasionOptions = [
  "My Engagement",
  "My Wedding",
  "My Daughter/Son's Wedding",
  "An Important Family Occasion",
  "Another Important Occasion",
];

const investmentByOccasion = {
  "My Engagement": [
    "USD 20,000 to 35,000",
    "USD 35,000 to 60,000",
    "Above USD 60,000",
  ],
  "My Wedding": [
    "USD 30,000 to 50,000",
    "USD 50,000 to 80,000",
    "USD 80,000 to 150,000",
    "Above USD 150,000",
  ],
  "My Daughter/Son's Wedding": [
    "USD 20,000 to 35,000",
    "USD 35,000 to 60,000",
    "Above USD 60,000",
  ],
  "An Important Family Occasion": [
    "USD 20,000 to 35,000",
    "USD 35,000 to 60,000",
    "Above USD 60,000",
  ],
  "Another Important Occasion": [
    "USD 20,000 to 35,000",
    "USD 35,000 to 60,000",
    "Above USD 60,000",
  ],
};

const priorOptions = [
  "Yes, more than once",
  "Once",
  "This is my first commission",
];

const initialForm = {
  originalityAck: false,
  firstName: "",
  lastName: "",
  email: "",
  location: "",
  mobile: "",
  investmentAck: false,
  occasion: "",
  investmentRange: "",
  story: "",
  imageName: "",
  imageData: "",
  measurements: "",
  timingAck: false,
  deliveryDate: "",
  priorCommission: "",
  draws: "",
};

const SectionDivider = () => (
  <div style={{ height: 1, background: "rgba(61,54,48,0.12)", margin: "40px 0" }} />
);

const Waitlist = () => {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const investmentOptions = useMemo(
    () => (form.occasion ? investmentByOccasion[form.occasion] : []),
    [form.occasion]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is too large. Please upload an image under 5 MB.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, imageName: file.name, imageData: reader.result }));
    };
    reader.onerror = () => {
      setError("Could not read the selected image. Please try another file.");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.originalityAck || !form.investmentAck || !form.timingAck) {
      setError("Please review and accept all acknowledgements.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again in a moment.";
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again in a moment.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AnnouncementBar />
      <Header />

      <div className="waitlist-hero">
        <div className="waitlist-hero-copy">
          <p>
            Label Binduu Jain is a private couture house. Each gown is composed around a woman&apos;s own story and designed for her alone. Wrought by hand across many months. Never repeated. Never replicated.
          </p>
        </div>
        <div
          className="waitlist-hero-image"
          style={{ backgroundImage: `url(${waitlistImage})` }}
        />
      </div>

      <div className="page-container" style={{ maxWidth: 720 }}>
        {submitted ? (
          <div className="thank-you-card">
            <h1 className="page-title" style={{ marginBottom: 20, textAlign: "center" }}>
              Thank you for joining the Label Binduu Jain waiting list.
            </h1>
            <p>
              Your request has been received and quietly noted. When an opening aligns with your requirements, our team will write to you directly.
            </p>
            <p>
              At Label Binduu Jain, only a handful of commissions are accepted each season &mdash; so that every work we do undertake may receive our full sincerity, commitment, and artistry.
            </p>
            <p style={{ marginBottom: 0 }}>We are grateful for your patience and understanding.</p>
          </div>
        ) : (
          <>
            <div className="section-line" />
            <h1 className="page-title">Waitlist Registration</h1>
            <div className="page-body" style={{ marginBottom: 40 }}>
              <p>
                A limited number of commissions are accepted each year. This registration is a request, not a booking &mdash; invitations are extended selectively. Every entry is read personally, and we respond when both availability and creative alignment allow.
              </p>
            </div>

            {error && (
              <div className="form-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="waitlist-form">
              {/* Originality acknowledgement */}
              <label className="ack">
                <input
                  type="checkbox"
                  checked={form.originalityAck}
                  onChange={(e) => set("originalityAck", e.target.checked)}
                />
                <span>
                  I understand that Label Binduu Jain creates original works only. I am commissioning a new design, not a reproduction of any existing gown. Past creations may inspire elements &mdash; never the whole.<span className="req">*</span>
                </span>
              </label>

              <div className="grid-2">
                <div className="form-field">
                  <label>First Name<span className="req">*</span></label>
                  <input required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Last Name<span className="req">*</span></label>
                  <input required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
                </div>
              </div>

              <div className="form-field">
                <label>Email<span className="req">*</span></label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>

              <div className="grid-2">
                <div className="form-field">
                  <label>Your Location<span className="req">*</span></label>
                  <select required value={form.location} onChange={(e) => set("location", e.target.value)}>
                    <option value="">Please Select</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Mobile Number<span className="req">*</span></label>
                  <input required value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+971 ..." />
                </div>
              </div>

              <SectionDivider />

              {/* Investment acknowledgement */}
              <label className="ack">
                <input
                  type="checkbox"
                  checked={form.investmentAck}
                  onChange={(e) => set("investmentAck", e.target.checked)}
                />
                <span>
                  I understand that Label Binduu Jain commissions commence at USD 20,000 for evening couture and USD 30,000 for bridal couture, with the final investment shaped by design, materials, and craftsmanship.<span className="req">*</span>
                </span>
              </label>

              <p className="hint">
                Every commission is priced according to its design, its materials, and the craftsmanship it demands. The range you select guides all that we will imagine for you.
              </p>

              <div className="grid-2">
                <div className="form-field">
                  <label>Your Occasion<span className="req">*</span></label>
                  <select
                    required
                    value={form.occasion}
                    onChange={(e) => {
                      set("occasion", e.target.value);
                      set("investmentRange", "");
                    }}
                  >
                    <option value="">Please Select</option>
                    {occasionOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Your Investment Range<span className="req">*</span></label>
                  <select
                    required
                    disabled={!form.occasion}
                    value={form.investmentRange}
                    onChange={(e) => set("investmentRange", e.target.value)}
                  >
                    <option value="">{form.occasion ? "Please Select" : "Select an occasion first"}</option>
                    {investmentOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <SectionDivider />

              {/* Your Story */}
              <h3 className="sub-heading">Your Story</h3>
              <p className="hint" style={{ marginTop: 6 }}>
                This is the most considered part of your request. Your words here are what we read most closely &mdash; they reveal your taste, your world, and whether the house can compose something true to you. A memory, a place, a feeling you wish to wear. Take your time here. What you write is where your gown begins.
                <span className="req">*</span>
              </p>
              <p className="hint" style={{ fontStyle: "italic" }}>
                Some begin with a childhood memory. Others with a piece their mother once wore. Some with the moment they first met, or the day a child was born.
              </p>
              <div className="form-field">
                <textarea
                  required
                  rows={6}
                  value={form.story}
                  onChange={(e) => set("story", e.target.value)}
                  placeholder="Your story begins here..."
                />
              </div>

              <p className="hint">
                Share images of couture you have worn or previously commissioned, along with any references you wish us to hold in mind. Both help us see your taste, and imagine what is uniquely yours.
              </p>

              <div className="upload-field">
                <label className="upload-btn">
                  <Upload size={16} strokeWidth={1.4} />
                  <span>Upload image</span>
                  <input type="file" accept="image/*" onChange={handleFile} hidden />
                </label>
                {form.imageName && (
                  <span className="upload-file"><Check size={14} /> {form.imageName}</span>
                )}
              </div>

              <SectionDivider />

              {/* Practical Details */}
              <h3 className="sub-heading">Practical Details</h3>
              <div className="form-field">
                <label>Your dress size, height, weight<span className="req">*</span></label>
                <input
                  required
                  value={form.measurements}
                  onChange={(e) => set("measurements", e.target.value)}
                  placeholder="e.g. Size 6 / 170 cm / 58 kg"
                />
              </div>

              <label className="ack">
                <input
                  type="checkbox"
                  checked={form.timingAck}
                  onChange={(e) => set("timingAck", e.target.checked)}
                />
                <span>
                  I acknowledge that a Label Binduu Jain creation cannot be hurried. Evening couture asks three to six months, bridal couture four to six, and intricate work may ask for longer. My occasion allows for this.<span className="req">*</span>
                </span>
              </label>

              <p className="hint">
                Every Label Binduu Jain commission opens with a private virtual consultation, wherever you may be in the world. It is here that your story is heard and your gown first imagined. Once the commission is confirmed, the atelier opens to you &mdash; fabrics, embroidery, and fittings arranged in quiet devotion to the work at hand. Before the final creation begins, a handcrafted toile, your gown in its rawest form, invites you to see and approve every line before the silk is cut.
              </p>

              <div className="grid-2">
                <div className="form-field">
                  <label>Expected Delivery Date<span className="req">*</span></label>
                  <input
                    required
                    type="date"
                    value={form.deliveryDate}
                    onChange={(e) => set("deliveryDate", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Have you commissioned bespoke couture before?</label>
                  <select
                    value={form.priorCommission}
                    onChange={(e) => set("priorCommission", e.target.value)}
                  >
                    <option value="">Please Select</option>
                    {priorOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label>What draws you to Label Binduu Jain?<span className="req">*</span></label>
                <textarea
                  required
                  rows={4}
                  value={form.draws}
                  onChange={(e) => set("draws", e.target.value)}
                  placeholder="Is it the artistry, the craftsmanship, the philosophy? Tell us, in your own words, what resonates."
                />
              </div>

              <div style={{ marginTop: 40, textAlign: "center" }}>
                <button type="submit" className="btn-primary" style={{ padding: "18px 56px" }} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Your Story"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <Footer />
    </>
  );
};

export default Waitlist;
